// src/features/editor/lib/mascotSlots.ts
import type { Mascot, MascotInput } from '@/types/course'

// `mascots` is a FULL REPLACE on the backend (docs/backend/gamified-course-update-fields.md):
// whatever array a PUT carries becomes the entire list. The map UI only shows two slots
// (indices 0/1) plus an "extras" row for index >= 2, so every mutation MUST start from the
// full current list and rebuild it whole — never send just the slots/row the widget can see.
// Список берётся из ЧЕРНОВИКА MapSection, а не из course-пропа: тот догоняет только после
// рефетча всего курса, и второе действие в этом окне считало бы от устаревшего списка.
// Поэтому операция возвращает Mascot[] — один и тот же список и рисуется, и уходит в патч
// (через toMascotInputs).
export type MascotOp =
  | { type: 'setSlot'; slot: number; image: string }
  | { type: 'removeAt'; index: number }
  | { type: 'append'; image: string }
  | { type: 'setPrompt'; index: number; prompt: string }

// У только что загруженного маскота серверного id ещё нет, а ряду «Остальные маскоты» нужен
// стабильный React-ключ (индекс запрещён: удаление из середины перепутало бы DOM-состояние).
// Отрицательные id не пересекаются с серверными и наружу не уходят — в патче только image.
let clientIdSeq = 0
const newClientId = (): number => {
  clientIdSeq -= 1
  return clientIdSeq
}

export const toMascotInputs = (list: Mascot[]): MascotInput[] =>
  list.map((m) => ({ image: m.image, imagePrompt: m.imagePrompt ?? '' }))

export const applyMascotOp = (current: Mascot[], op: MascotOp): Mascot[] => {
  const next = [...current]

  if (op.type === 'removeAt') {
    if (op.index < next.length) next.splice(op.index, 1)
    return next
  }

  if (op.type === 'append') {
    next.push({ id: newClientId(), image: op.image })
    return next
  }

  if (op.type === 'setPrompt') {
    const target = next[op.index]
    // Маскота может уже не быть (удалили, пока правился промпт) — молча ничего
    // не делаем: создать его здесь значило бы вернуть удалённого без картинки.
    if (target) next[op.index] = { ...target, imagePrompt: op.prompt }
    return next
  }

  const existing = next[op.slot]
  // Разворот, а не пересборка из двух полей: `mascots` — полная замена списка,
  // и всё, что здесь не перенесено (сегодня `imagePrompt`), уходит в PUT
  // пустым и стирается на сервере. Замена картинки промпт не отменяет.
  if (existing) next[op.slot] = { ...existing, image: op.image }
  else next.push({ id: newClientId(), image: op.image })
  return next
}

/** Первое место маскота открывается с 5 уроков, дальше по одному на каждые 3: 5, 8, 11, 14 и так далее. */
export const MASCOT_FIRST_SLOT_LESSONS = 5
export const MASCOT_LESSONS_STEP = 3

/** Сколько уроков нужно, чтобы открылось место маскота с этим индексом (с нуля). */
export const lessonsToUnlockSlot = (index: number): number =>
  MASCOT_FIRST_SLOT_LESSONS + index * MASCOT_LESSONS_STEP

/**
 * Сколько мест рисовать на карте: все открытые и одно следующее закрытое (чтобы было видно,
 * сколько уроков до него), но не меньше числа уже загруженных маскотов — у курса могут лежать
 * маскоты сверх того, что открывает нынешнее число уроков.
 */
export const mascotSlotsToShow = (lessons: number, mascots: number): number =>
  Math.max(Math.floor((lessons - MASCOT_FIRST_SLOT_LESSONS) / MASCOT_LESSONS_STEP) + 2, mascots, 1)

/**
 * Сколько мест реально рисует карта: открытые и уже занятые, без следующего закрытого.
 * Общее число для карты и списка промптов — маскот живёт ровно в одном из двух мест.
 */
export const mascotMapSlotCount = (lessons: number, mascots: number): number =>
  Math.max(mascotSlotsToShow(lessons, mascots) - 1, mascots)
